// frontend/src/pages/WaiverSigningPage.js - UPDATED WITH DYNAMIC WAIVER
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import API from '../services/api';

const WaiverSigningPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [waiver, setWaiver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullName, setFullName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  // Load dynamic waiver content
  const { data: waiverContent, loading: waiverContentLoading } = useApi(() => API.content.getWaiverContent());

  useEffect(() => {
    loadWaiver();
  }, [token]);

  const loadWaiver = async () => {
    try {
      setLoading(true);
      const response = await API.bookings.getWaiverByToken(token);
      setWaiver(response.data);
      setFullName(response.data.full_name); // Pre-fill with participant name
    } catch (err) {
      setError(err.message || 'Invalid or expired waiver link');
    } finally {
      setLoading(false);
    }
  };

  const handleSignWaiver = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      setError('Please accept the terms and conditions');
      return;
    }

    if (fullName.trim().toLowerCase() !== waiver.full_name.trim().toLowerCase()) {
      setError('Name must match exactly: ' + waiver.full_name);
      return;
    }

    try {
      setSigning(true);
      setError(null);
      
      await API.bookings.signWaiverByToken(token, fullName);
      setSigned(true);
    } catch (err) {
      setError(err.message || 'Failed to sign waiver');
    } finally {
      setSigning(false);
    }
  };

  // Show loading state while loading participant OR waiver content
  if (loading || waiverContentLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-white">Loading waiver...</p>
        </div>
      </div>
    );
  }

  if (error && !waiver) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="bg-gray-800 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">Invalid Link</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-bold transition-colors"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-gray-900 py-20 flex items-center justify-center px-4">
        <div className="bg-gray-800 rounded-2xl p-8 max-w-2xl w-full text-center">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-emerald-600 rounded-full mb-6 animate-bounce">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <h2 className="text-3xl font-playfair font-bold text-white mb-4">
            Waiver Signed Successfully! ✅
          </h2>
          <p className="text-gray-300 mb-2">
            Thank you for signing the waiver, <strong>{waiver.full_name}</strong>!
          </p>
          <p className="text-gray-400 mb-8">
            The tour organizer (<strong>{waiver.booked_by}</strong>) has been notified.
          </p>
          
          <div className="bg-gray-700 rounded-xl p-6 mb-6 text-left">
            <h3 className="font-bold text-white mb-4 text-center">Tour Details:</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-3 border-b border-gray-600">
                <span className="text-gray-400">Tour:</span>
                <span className="font-bold text-white">{waiver.tour_name}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-gray-600">
                <span className="text-gray-400">Date:</span>
                <span className="font-bold text-white">{waiver.formatted_date}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-gray-600">
                <span className="text-gray-400">Time:</span>
                <span className="font-bold text-white">{waiver.formatted_time}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-gray-600">
                <span className="text-gray-400">Duration:</span>
                <span className="font-bold text-white">{waiver.tour_duration}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Booking Ref:</span>
                <span className="font-bold text-emerald-400">{waiver.booking_reference}</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-600/20 border border-blue-500 rounded-xl p-4 mb-6">
            <p className="text-blue-200 text-sm">
              <strong>What's Next?</strong> Arrive 15 minutes early at your pickup location on the tour date. Bring comfortable clothing, closed-toe shoes, and don't forget your camera! 📸
            </p>
          </div>

          <p className="text-gray-400 text-sm">
            You can close this page now. See you on the tour! 🏍️
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header with Tour Info */}
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 rounded-t-2xl p-8 text-white">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-playfair font-bold mb-2">
              Digital Waiver Signature Required
            </h1>
            <p className="text-emerald-100">
              Please review and sign the waiver to complete your booking
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <h3 className="font-bold mb-4 text-center text-lg">Your Tour Information:</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-emerald-100 text-sm mb-1">Tour Name:</p>
                <p className="font-bold">{waiver.tour_name}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-emerald-100 text-sm mb-1">Duration:</p>
                <p className="font-bold">{waiver.tour_duration}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-emerald-100 text-sm mb-1">Date:</p>
                <p className="font-bold">{waiver.formatted_date}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-emerald-100 text-sm mb-1">Time:</p>
                <p className="font-bold">{waiver.formatted_time}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-emerald-100 text-sm mb-1">Booking Reference:</p>
                <p className="font-bold">{waiver.booking_reference}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-emerald-100 text-sm mb-1">Booked By:</p>
                <p className="font-bold">{waiver.booked_by}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Waiver Content - NOW DYNAMIC */}
        <div className="bg-gray-700 p-8">
          <div className="bg-white rounded-xl p-6 max-h-96 overflow-y-auto mb-6">
            {waiverContent ? (
              <>
                <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-4 text-center">
                  {waiverContent.title}
                </h2>
                <p className="text-sm text-gray-600 mb-6 text-center">
                  Layou Adventures - St. Vincent and the Grenadines<br/>
                  Version: {waiverContent.version} | Effective: {new Date(waiverContent.effective_date).toLocaleDateString()}
                </p>
                
                {/* Render dynamic HTML waiver content */}
                <div 
                  className="waiver-content prose prose-sm max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: waiverContent.content }}
                />
              </>
            ) : (
              // Fallback to basic message if waiver content not available
              <div className="text-center py-8">
                <p className="text-red-600 font-bold mb-4">Unable to load waiver content.</p>
                <p className="text-gray-600">Please contact support at info@layouadventures.com</p>
              </div>
            )}
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-200 flex items-start gap-3">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Signature Form */}
          <form onSubmit={handleSignWaiver} className="bg-gray-800 rounded-xl p-6">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Electronic Signature
            </h3>
            
            {/* Agreement Checkbox */}
            <div className="mb-6 bg-gradient-to-r from-gray-700 to-gray-700 p-5 rounded-lg border-2 border-emerald-500/50">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-6 h-6 text-emerald-600 border-gray-400 rounded focus:ring-emerald-500 focus:ring-2 cursor-pointer"
                />
                <span className="text-gray-100 text-sm leading-relaxed">
                  <strong className="text-white block mb-2 text-base">
                    ✓ I agree to the terms and conditions
                  </strong>
                  I have read and understood the complete liability waiver above. I agree to all terms and conditions and acknowledge that I am signing this agreement voluntarily. By typing my name below, I am providing my electronic signature which has the same legal effect as a handwritten signature.
                </span>
              </label>
            </div>

            {/* Name Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Type your full name to sign *
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={!agreedToTerms}
                className="w-full px-4 py-4 bg-gray-600 border-2 border-gray-500 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-medium transition-all"
                placeholder="Enter your full name"
              />
              <p className="text-sm text-gray-400 mt-2">
                Must match: <strong className="text-white">{waiver.full_name}</strong>
              </p>
            </div>

            {/* Warning Box */}
            <div className="mb-6 p-4 bg-yellow-500/20 border border-yellow-500 rounded-lg">
              <p className="text-yellow-200 text-sm">
                <strong>⚠️ Important:</strong> By signing this waiver, you confirm that you understand and accept all risks associated with ATV tours. This is a legally binding agreement.
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={signing || !agreedToTerms || !fullName || !waiverContent}
              className="w-full py-4 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 rounded-lg font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg"
            >
              {signing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                  Signing Waiver...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Sign Waiver
                </>
              )}
            </button>

            <p className="text-center text-gray-400 text-xs mt-4">
              By clicking "Sign Waiver", you confirm that all information is accurate and complete.
            </p>
          </form>
        </div>

        {/* Footer */}
        <div className="bg-gray-800 rounded-b-2xl p-6 text-center text-gray-400 text-sm">
          <p className="mb-2">
            Questions? Contact us at{' '}
            <a href="mailto:info@layouadventures.com" className="text-emerald-400 hover:text-emerald-300 underline">
              info@layouadventures.com
            </a>
            {' '}or{' '}
            <a href="tel:+16143852846" className="text-emerald-400 hover:text-emerald-300 underline">
              (614) 385-2846
            </a>
          </p>
          <p className="text-xs text-gray-500">
            © 2025 Layou Adventures. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WaiverSigningPage;